import React, { useEffect, useState } from "react";
import { useOutletsQuery } from "../../Store/Apis/outletApi";
import { useRolesQuery } from "../../Store/Apis/rolePermissionApi";

const AddEditUserForm = ({ userData, onSubmit }) => {
  const roleApi = useRolesQuery();
  const outletApi = useOutletsQuery();
  const [formData, setFormData] = useState({
    roles: "",
    name: "",
    phoneNumber: "",
    email: "",
    outlet: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [isPasswordUpdate, setIsPasswordUpdate] = useState(false); // New state for password update checkbox

  // Populate form fields if userData is provided (for editing)
  useEffect(() => {
    if (userData) {
      setFormData({
        roles: userData.roles || "",
        name: userData.name || "",
        phoneNumber: userData.phoneNumber || "",
        email: userData.email || "",
        outlet: userData.outlet || "",
        password: userData.password || "",
      });
    }
  }, [userData]);

  // Validate form fields
  const validateForm = () => {
    const newErrors = {};

    if (!formData.roles) newErrors.roles = "User Type is required.";
    if (!formData.name.trim()) newErrors.name = "Name is required.";
    else if (formData.name.length < 3)
      newErrors.name = "Name must be at least 3 characters long.";

    if (!formData.phoneNumber.trim())
      newErrors.phoneNumber = "Phone number is required.";
    else if (formData.phoneNumber.replace(/\D/g, "").length !== 10)
      newErrors.phoneNumber = "Phone number must be exactly 10 digits.";

    if (!formData.email.trim()) newErrors.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Email format is invalid.";

    if (!formData.outlet) newErrors.outlet = "Outlet is required.";
    if (!formData.password && (!userData || isPasswordUpdate))
      newErrors.password = "Password is required.";

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear specific field error when user starts typing/correcting
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const formErrors = validateForm();

    if (Object.keys(formErrors).length === 0) {
      onSubmit(formData);
    } else {
      setErrors(formErrors);
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <form onSubmit={handleSubmit} noValidate>
          <div className="row gy-4">
            {/* Outlet */}
            <div className="col-md-6">
              <label className="form-label">
                Outlet <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${errors.outlet ? "is-invalid" : ""}`}
                id="outlet"
                name="outlet"
                value={formData.outlet}
                onChange={handleChange}
                disabled={!outletApi.isSuccess}
              >
                <option value="">Select Outlet</option>
                {outletApi?.data?.data?.map((elem, i) => (
                  <option value={elem.outletId} key={i}>
                    {elem.outletName}
                  </option>
                ))}
              </select>
              {errors.outlet && (
                <div className="invalid-feedback">{errors.outlet}</div>
              )}
            </div>

            {/* User Role */}
            <div className="col-md-6">
              <label className="form-label">
                Roles <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${errors.roles ? "is-invalid" : ""}`}
                id="roles"
                name="roles"
                value={formData.roles}
                onChange={handleChange}
                disabled={!roleApi.isSuccess}
              >
                <option value="">Select Role</option>
                {roleApi?.data?.data.map((elem, i) => (
                  <option value={elem.roleID} key={i}>
                    {elem.roleName}
                  </option>
                ))}
              </select>
              {errors.roles && (
                <div className="invalid-feedback">{errors.roles}</div>
              )}
            </div>

            {/* Name */}
            <div className="col-md-6">
              <label className="form-label">
                Full Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.name ? "is-invalid" : ""}`}
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
              {errors.name && (
                <div className="invalid-feedback">{errors.name}</div>
              )}
            </div>

            {/* Phone Number */}
            <div className="col-md-6">
              <label className="form-label">
                Phone Number <span className="text-danger">*</span>
              </label>
              <input
                type="tel"
                className="form-control"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="Enter 10-digit phone number"
              />
              {errors.phoneNumber && (
                <div className="text-danger small mt-1">
                  {errors.phoneNumber}
                </div>
              )}
            </div>

            {/* Email */}
            <div className="col-md-6">
              <label className="form-label">
                Email <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                className={`form-control ${errors.email ? "is-invalid" : ""}`}
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && (
                <div className="invalid-feedback">{errors.email}</div>
              )}
            </div>

            {/* Password Update Checkbox */}
            <div className="col-md-12">
              <div className="row">
                <div className="col-md-6">
                  {userData && (
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={isPasswordUpdate}
                        onChange={() => setIsPasswordUpdate(!isPasswordUpdate)}
                        id="changePassword"
                      />
                      <label
                        className="form-check-label"
                        htmlFor="changePassword"
                      >
                        Change Password?
                      </label>
                    </div>
                  )}
                  {/* Password Field */}
                  {(isPasswordUpdate || !userData) && (
                    <>
                      <label className="form-label">
                        Password <span className="text-danger">*</span>
                      </label>
                      <input
                        type={isPasswordUpdate ? "text" : "password"}
                        className={`form-control ${
                          errors.password ? "is-invalid" : ""
                        }`}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                      />
                      {errors.password && (
                        <div className="invalid-feedback">
                          {errors.password}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="col-12 text-center">
              <button type="submit" className="btn btn-primary">
                {userData ? "Update User" : "Add User"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditUserForm;
